<script setup>
import { watch, onMounted, ref, computed } from 'vue';
import { useCountriesStore } from './stores/countries';
import { useBusinessModelTypologiesStore } from './stores/businessModelTypologies';
import { useGovernanceLevelsStore } from './stores/governanceLevels';
import { useProcurementTypesStore } from './stores/procurementTypes';
import { useElectricRequirementClassesStore } from './stores/electricRequirementClasses'
import { useRevenueSourcesStore } from './stores/revenueSources';
import { useContractDurationsStore } from './stores/contractDurations';
import { useCitiesStore } from './stores/cities';
import { useCityPopulationRangesStore } from './stores/cityPopulationRanges';
import apiFetch from '@wordpress/api-fetch';
import HorizontalBarChart from './components/HorizontalBarChart.vue';
import Filters from './components/Filters.vue';
import { useRouteLoadingStore } from './stores/routeLoading';

const routeLoadingStore = useRouteLoadingStore();
const businessModelTypologiesStore = useBusinessModelTypologiesStore();
const governanceLevelsStore = useGovernanceLevelsStore();
const procurementTypesStore = useProcurementTypesStore();
const electricRequirementClassesStore = useElectricRequirementClassesStore();
const revenueSourcesStore = useRevenueSourcesStore();
const contractDurationsStore = useContractDurationsStore();
const countriesStore = useCountriesStore();
const citiesStore = useCitiesStore();
const cityPopulationRangesStore = useCityPopulationRangesStore();
const lang = document.documentElement.lang?.substring(0, 2);

const chartProcurementTypes = computed(() => {
  const active = procurementTypesStore.activeProcurementTypes;
  if (!active.length) return procurementTypesStore.procurementTypes;
  return procurementTypesStore.procurementTypes.filter(item => active.includes(item.slug));
});

const chartBusinessModelTypologies = computed(() => {
  const active = businessModelTypologiesStore.activeBusinessModelTypologies;
  if (!active.length) return businessModelTypologiesStore.businessModelTypologies;
  return businessModelTypologiesStore.businessModelTypologies.filter(item => active.includes(item.slug));
});
const strings = ref(null);
const isFiltersLoading = ref(false);
const chartsReady = ref(false);
const isMobile = window.innerWidth < 1024;

const hasNoChartData = computed(() => {
  return chartsReady.value
    && !isFiltersLoading.value
    && chartProcurementTypes.value.length === 0
    && chartBusinessModelTypologies.value.length === 0
    && governanceLevelsStore.governanceLevels.length === 0
    && contractDurationsStore.contractDurations.length === 0
    && electricRequirementClassesStore.electricRequirementClasses.length === 0
    && revenueSourcesStore.revenueSources.length === 0;
});

onMounted(async () => {
  try {
    const translations = await apiFetch({
      path: `/wp-json/ebus/market/translations?lang=${lang}`,
    });

    strings.value = translations;
  } catch (error) {
    console.error('Error: ', error);
  }

  try {
    await Promise.all([
      procurementTypesStore.loadProcurementTypes({ lang }),
      businessModelTypologiesStore.loadBusinessModelTypologies({ lang }),
      governanceLevelsStore.loadGovernanceLevels({ lang }),
      contractDurationsStore.loadContractDurations({ lang }),
      electricRequirementClassesStore.loadElectricRequirementClasses({ lang }),
      revenueSourcesStore.loadRevenueSources({ lang }),
    ]);
  } catch (error) {
    console.error('Error: ', error);
  } finally {
    routeLoadingStore.stop();
  }

  chartsReady.value = true;

  countriesStore.loadCountries({ lang });
  citiesStore.loadCities({ lang });
  cityPopulationRangesStore.loadRanges({ lang });
});

watch(() => [countriesStore.activeCountries, citiesStore.activeCities, cityPopulationRangesStore.activeRanges, procurementTypesStore.activeProcurementTypes, businessModelTypologiesStore.activeBusinessModelTypologies], async () => {
    isFiltersLoading.value = true;

    try {
      const params = {
        lang,
        country: countriesStore.activeCountries,
        city: citiesStore.activeCities,
        city_population_range: cityPopulationRangesStore.activeRanges,
        procurement_type: procurementTypesStore.activeProcurementTypes,
        business_model_typology: businessModelTypologiesStore.activeBusinessModelTypologies,
      };

      await Promise.all([
        procurementTypesStore.loadProcurementTypes(params),
        businessModelTypologiesStore.loadBusinessModelTypologies(params),
        governanceLevelsStore.loadGovernanceLevels(params),
        contractDurationsStore.loadContractDurations(params),
        electricRequirementClassesStore.loadElectricRequirementClasses(params),
        revenueSourcesStore.loadRevenueSources(params),
      ]);
    } catch (error) {
      console.error('Error: ', error);
    } finally {
      isFiltersLoading.value = false;
    }
  }, { deep: true });
</script>

<template>
  <div v-if="strings" class="w-full! min-h-screen! flex! flex-col! bg-light-blue-2!">
    <div class="w-full! flex!">
      <div class="hidden! lg:flex! flex-col! gap-y-4! w-full! lg:min-w-75! lg:max-w-75! h-screen! bg-light-blue-3! p-6! lg:sticky! lg:top-0! lg:self-start!">
        <Filters :is-loading="isFiltersLoading" />
      </div>

      <div class="w-full! flex! flex-col! gap-y-8! px-6 lg:px-12 py-6!">
        <div class="flex! flex-col! gap-y-4!">
          <h2 class="font-ibm-plex-mono! font-medium! text-4xl! leading-tight! text-black!">{{ strings.tool_sample_analytics }}</h2>
          <p class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
            {{ strings.analytics_description }}
          </p>
        </div>

        <div class="flex! flex-col! gap-y-12! lg:gap-y-8!">
          <HorizontalBarChart
            :title="strings.procurement_type"
            :data="chartProcurementTypes"
            :is-column="isMobile"
            :show-countries="true"
            :empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
          />

          <HorizontalBarChart
            :title="strings.business_model_typology"
            :data="chartBusinessModelTypologies"
            :is-column="isMobile"
            :show-countries="true"
            :empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
          />

          <HorizontalBarChart
            :title="strings.governance_arrangement_level"
            :data="governanceLevelsStore.governanceLevels"
            :is-column="isMobile"
            :show-countries="true"
            :empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
          />

          <HorizontalBarChart
            :title="strings.contract_duration"
            :data="contractDurationsStore.contractDurations"
            :is-column="isMobile"
            :show-countries="true"
            :empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
          />

          <HorizontalBarChart
            :title="strings.electric_requirements"
            :data="electricRequirementClassesStore.electricRequirementClasses"
            :is-column="isMobile"
            :show-countries="true"
            :empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
          />

          <HorizontalBarChart
            :title="strings.payment_or_revenue_sources"
            :data="revenueSourcesStore.revenueSources"
            :footnote="strings.not_exclusive"
            :is-column="isMobile"
            :show-countries="true"
            :empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
          />
        </div>

        <div class="mt-auto! pt-27.5! flex! justify-end!">
          <div class="flex! items-center! gap-x-5!">
            <router-link to="/references" class="font-ibm-plex-mono! font-medium! text-black! text-lg! leading-tight! no-underline!">
              {{ strings.references }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
